/**
 * Town & Country — colourway hard navigation.
 *
 * WHY THIS EXISTS
 * ---------------
 * The client built colourways as separate products ("… - Washed Black",
 * "… - Vintage White") rather than variants, and they're stitched together with
 * a Shopify Combined Listing. When you click a colour that belongs to a
 * different product, Horizon does NOT reload the page — it fetches the new
 * product via the Section Rendering API and morphs the whole of <main>
 * (assets/variant-picker.js, `loadsNewProduct` → `morphElementSelector = 'main'`).
 *
 * Measured on the live store, that leaves the PREVIOUS colourway's title, price
 * and availability on screen for 550–1200ms, with no loading state of any kind,
 * so it reads as current when it isn't. It also skips a page view (Klaviyo /
 * Triple Whale attribution) and gives Wholesale Gorilla and Judge.me no chance
 * to re-run against the new product.
 *
 * This turns those cross-product clicks back into ordinary navigations. A page
 * load is honest: the browser shows its own progress, and everything on the
 * page is correct on arrival.
 *
 * SCOPE — it only ever fires for combined-listing colour changes:
 *   - Ordinary variant changes (size, etc.) are untouched and stay instant.
 *   - Clicking the colour you're already on is untouched.
 *   - Product cards and quick-add dialogs are untouched.
 *
 * SELF-DISABLING. When the client rebuilds these as real variants (expected
 * ~Feb 2027), `product_option_value.product_url` returns nil, so Horizon
 * renders `data-connected-product-url=""` — present but EMPTY, not absent. The
 * empty check below then excludes every option and this file becomes inert with
 * no action required. Deleting it is tidy-up, not a migration step.
 */
(function () {
  'use strict';

  /**
   * The product URL an option value points at, or '' for ordinary variants.
   * Handles both swatch/button radios and the dropdown variant style.
   * @param {EventTarget | null} target
   * @returns {string}
   */
  function connectedProductUrl(target) {
    if (target instanceof HTMLSelectElement) {
      var option = target.options[target.selectedIndex];
      return (option && option.dataset.connectedProductUrl) || '';
    }

    if (target instanceof HTMLInputElement && target.type === 'radio') {
      return target.dataset.connectedProductUrl || '';
    }

    return '';
  }

  /**
   * Every currently selected option value id, in DOM order.
   *
   * These are read live rather than from Horizon's own `selectedOptionsValues`
   * getter, which looks at `select option[selected]` — the server-rendered
   * attribute, not the user's current choice.
   *
   * Each child product owns its own size option values, so the ids we pass
   * belong to the product we're leaving, not the one we're going to. Shopify
   * resolves them by option value name anyway, so the selected size carries
   * across correctly (verified against this store).
   *
   * @param {Element} picker
   * @returns {string[]}
   */
  function selectedOptionValueIds(picker) {
    var ids = [];

    picker.querySelectorAll('fieldset input:checked, select').forEach(function (element) {
      var node = element instanceof HTMLSelectElement ? element.options[element.selectedIndex] : element;
      var id = node instanceof HTMLElement ? node.dataset.optionValueId : null;

      if (id) ids.push(id);
    });

    return ids;
  }

  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!(target instanceof HTMLElement)) return;

      var picker = target.closest('variant-picker');
      if (!picker) return;

      // Mirrors the `isOnProductPage` test in assets/variant-picker.js so we
      // intercept exactly when Horizon would otherwise morph across products.
      if (picker.getAttribute('data-template-product-match') !== 'true') return;
      if (target.closest('product-card') || target.closest('quick-add-dialog')) return;

      var newUrl = connectedProductUrl(target);
      if (!newUrl) return; // ordinary variant — leave Horizon alone

      var currentUrl = (picker.getAttribute('data-product-url') || window.location.pathname).split('?')[0];
      if (newUrl === currentUrl) return; // same product, nothing to navigate to

      // Stop Horizon's own change handler (bound on <variant-picker>, which is
      // an ancestor) from starting a fetch we're about to make pointless.
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();

      var params = [];

      // Preserve the alternative-view parameter if one is in play.
      var view = new URLSearchParams(window.location.search).get('view');
      if (view) params.push('view=' + encodeURIComponent(view));

      var optionValueIds = selectedOptionValueIds(picker);
      if (optionValueIds.length) params.push('option_values=' + optionValueIds.join(','));

      window.location.href = params.length ? newUrl + '?' + params.join('&') : newUrl;
    },
    true // capture — must run before <variant-picker>'s own bubble-phase listener
  );
})();
